(() => {
  const headerSelector = '[data-ibh-header]';
  const drawerSelector = '[data-header-drawer]';
  const drawerToggleSelector = '[data-header-drawer-toggle]';
  const searchSelector = '[data-header-search]';
  const searchToggleSelector = '[data-header-search-toggle]';
  const searchFormSelector = '[data-header-search-form]';
  const searchInputSelector = '[data-header-search-input]';
  const searchResultsSelector = '[data-header-search-results]';

  const wildcardSearchTerm = (value) => {
    const query = value.trim();

    if (!query || query.endsWith('*') || /[:"]/u.test(query)) return query;

    return `${query}*`;
  };

  const submitSearchForm = (form, input) => {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const action = form.getAttribute('action') || window.location.pathname;

    params.set(input.name, wildcardSearchTerm(input.value));
    window.location.href = `${action}?${params.toString()}`;
  };

  const updateStickyOffset = () => {
    const announcementBar = document.querySelector(
      '[data-announcement-bar][data-sticky-behavior="sticky"], [data-announcement-bar][data-sticky-behavior="hide_on_scroll"]'
    );
    const visible = announcementBar && !announcementBar.hidden && !announcementBar.classList.contains('is-announcement-hidden');
    const offset = visible ? announcementBar.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--announcement-bar-sticky-offset', `${offset}px`);
  };

  const initStickyHeader = (header) => {
    if (header.dataset.stickyInitialized === 'true') return;

    header.dataset.stickyInitialized = 'true';
    updateStickyOffset();
    window.addEventListener('resize', updateStickyOffset, { passive: true });

    if (header.dataset.stickyBehavior !== 'hide_on_scroll') return;

    let lastScrollY = window.scrollY;
    let ticking = false;
    const threshold = 96;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;

      if (currentScrollY <= 0) {
        header.classList.remove('is-header-hidden');
      } else if (scrollingDown && currentScrollY > threshold) {
        header.classList.add('is-header-hidden');
      } else if (!scrollingDown) {
        header.classList.remove('is-header-hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        window.requestAnimationFrame(updateHeader);
        ticking = true;
      },
      { passive: true }
    );
  };

  const setDrawerState = (header, open) => {
    const drawer = header.querySelector(drawerSelector);
    const toggle = header.querySelector(drawerToggleSelector);

    if (!drawer || !toggle) return;

    drawer.hidden = !open;
    header.classList.toggle('is-drawer-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };

  const setSearchState = (search, open, focus = false) => {
    const form = search.querySelector(searchFormSelector);
    const toggle = search.querySelector(searchToggleSelector);
    const input = search.querySelector(searchInputSelector);
    const results = search.querySelector(searchResultsSelector);

    search.classList.toggle('is-search-open', open);

    if (form) form.hidden = toggle ? !open : false;
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    if (!open && results) results.hidden = true;
    if (open && focus && input) input.focus();
  };

  const closeSearches = (header, exceptSearch = null) => {
    header.querySelectorAll(searchSelector).forEach((search) => {
      if (search === exceptSearch) return;

      setSearchState(search, false);
    });
  };

  const initDrawer = (header) => {
    const toggle = header.querySelector(drawerToggleSelector);
    const drawer = header.querySelector(drawerSelector);

    if (!toggle || !drawer || toggle.dataset.drawerInitialized === 'true') return;

    toggle.dataset.drawerInitialized = 'true';

    toggle.addEventListener('click', () => {
      const willOpen = drawer.hidden;

      closeSearches(header);
      setDrawerState(header, willOpen);
    });

    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) setDrawerState(header, false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || drawer.hidden) return;

      setDrawerState(header, false);
      toggle.focus();
    });

    document.addEventListener('click', (event) => {
      if (drawer.hidden || header.contains(event.target)) return;
      setDrawerState(header, false);
    });
  };

  const initSearch = (scope) => {
    scope.querySelectorAll(searchSelector).forEach((search) => {
      if (search.dataset.searchInitialized === 'true') return;

      search.dataset.searchInitialized = 'true';

      const toggle = search.querySelector(searchToggleSelector);
      const form = search.querySelector(searchFormSelector);
      const input = search.querySelector(searchInputSelector);
      const results = search.querySelector(searchResultsSelector);
      const header = search.closest(headerSelector);
      const types = search.dataset.searchTypes ?? 'product';
      let timeout = null;
      let abortController = null;

      setSearchState(search, false);

      if (toggle && form) {
        toggle.addEventListener('click', () => {
          const willOpen = !search.classList.contains('is-search-open');

          closeSearches(document, search);
          if (header) setDrawerState(header, false);
          setSearchState(search, willOpen, willOpen);
        });
      }

      if (form && input) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          submitSearchForm(form, input);
        });
      }

      if (!input || !results) return;

      input.addEventListener('input', () => {
        const query = input.value.trim();

        window.clearTimeout(timeout);
        if (abortController) abortController.abort();

        if (query.length < 2 || !types) {
          results.hidden = true;
          results.replaceChildren();
          return;
        }

        timeout = window.setTimeout(() => {
          const params = new URLSearchParams();
          params.set('q', query);
          params.set('section_id', 'predictive-search');
          params.set('resources[type]', types);
          params.set('resources[limit]', '4');
          params.set('resources[options][unavailable_products]', 'last');

          if (abortController) abortController.abort();
          abortController = new AbortController();

          fetch(`${form.dataset.predictiveSearchUrl}?${params.toString()}`, {
            headers: { Accept: 'text/html' },
            signal: abortController.signal
          })
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => {
              if (!html) {
                results.hidden = true;
                return;
              }

              const doc = new DOMParser().parseFromString(html, 'text/html');
              const section = doc.querySelector('#shopify-section-predictive-search');
              const markup = section ? section.innerHTML : html;

              results.innerHTML = markup;
              results.hidden = !results.textContent.trim();
            })
            .catch((error) => {
              if (error.name !== 'AbortError') results.hidden = true;
            });
        }, 180);
      });

      document.addEventListener('click', (event) => {
        if (search.contains(event.target)) return;
        results.hidden = true;
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !search.classList.contains('is-search-open')) return;
        results.hidden = true;

        if (toggle && form) {
          setSearchState(search, false);
          toggle.focus();
        }
      });
    });
  };

  // Fit within the merchant's maximum; wrap once the readable floor is reached.
  const nameFitCleanup = new WeakMap();
  const initNameFit = (header) => {
    if (nameFitCleanup.has(header)) return;
    const name = header.querySelector('.ibh-header__shop-name');
    if (!name) return;
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return;
    let frame = 0;
    let disposed = false;
    const fit = () => {
      frame = 0;
      if (disposed || !name.isConnected) return;
      name.style.removeProperty('font-size');
      const style = getComputedStyle(name);
      const size = parseFloat(style.fontSize);
      const width = name.clientWidth;
      if (!width || !size) return;
      context.font = `${style.fontStyle} ${style.fontWeight} ${size}px ${style.fontFamily}`;
      const spacing = parseFloat(style.letterSpacing) || 0;
      const measure = (text) => context.measureText(text).width + Math.max(0, text.length - 1) * spacing;
      const text = name.textContent.trim().replace(/\s+/g, ' ');
      const longest = Math.max(...text.split(' ').map(measure), 1);
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const minimumScale = parseFloat(style.getPropertyValue('--ibh-header-name-min-scale')) || 0.65;
      const minimum = Math.min(size, Math.max(rootSize, size * minimumScale));
      const fitted = Math.max(minimum, size * Math.min(1, width / longest, width * 2 / Math.max(measure(text), 1)));
      if (fitted < size - 0.5) name.style.fontSize = `${fitted / rootSize}rem`;
    };
    const schedule = () => {
      if (!disposed && !frame) frame = requestAnimationFrame(fit);
    };
    let previousWidth = -1;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === previousWidth) return;
      previousWidth = entry.contentRect.width;
      schedule();
    });
    observer.observe(header.querySelector('.ibh-header__brand'));
    window.addEventListener('resize', schedule, { passive: true });
    document.fonts?.ready.then(schedule);
    schedule();
    nameFitCleanup.set(header, () => {
      disposed = true;
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
      nameFitCleanup.delete(header);
    });
  };

  const initHeader = (scope = document) => {
    scope.querySelectorAll(headerSelector).forEach((header) => {
      initNameFit(header);
      initStickyHeader(header);
      initDrawer(header);
    });
    initSearch(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeader());
  } else {
    initHeader();
  }

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(headerSelector).forEach((header) => nameFitCleanup.get(header)?.());
  });
  document.addEventListener('shopify:section:load', (event) => initHeader(event.target));
})();
